import { satisfies } from "semver";
import { useEffect } from "react";
import * as envService from "../services/env";
import { getAppDeps, getSharedFabricDeps } from "./deps.helpers";

const fabricDepCheckShownMap = {};

const prefix = "[@granular/fabric-core][useFabricDepCheck]";

const log = (...rest: any[]) => {
  console.log(prefix, ...rest);
};
const warn = (...rest: any[]) => {
  console.warn(prefix, ...rest);
};
const error = (...rest: any[]) => {
  console.error(prefix, ...rest);
};

export { getSharedFabricDeps, getAppDeps };

const SLACK_URI = "https://granular.slack.com/archives/CU5C80W5T";
const SUPPORT_URI = "https://fabric.granular.ag/docs/using-fabric/dependencies";

export const useFabricDepCheck = (appEnv: envService.Env): void => {
  useEffect(() => {
    try {
      // Disable when testing
      if (appEnv.NODE_ENV === "TEST") {
        return;
      }

      // Disable when in standalone mode
      if (envService.getIsInStandaloneMode()) {
        console.debug(
          "[@granular/fabric-core] useFabricDepCheck disabled due to standalone mode",
        );
        return;
      }

      const appName = envService.getAppName(appEnv);

      // Verify warnings are only shown once per app
      if (fabricDepCheckShownMap[appName]) {
        return;
      }
      fabricDepCheckShownMap[appName] = true;

      // Do not run in the container
      if (appName === "container") {
        console.error(
          `[@granular/fabric-core] useFabricDepCheck will not run in "container" as it is only meant to be run in apps`,
        );
        return;
      }

      const sharedFabricDeps = getSharedFabricDeps();
      const appDeps = getAppDeps(appEnv);

      /**
       * Filter out the dependencies provided by the Core from the App
       * dependencies before logging to avoid confusing developers.
       *
       * Also: We (Apollo) don't care what dependencies developers bring to the
       * party since they're overridden anyway (and we inform them that they
       * are in `useLogFabricProvidedDependencies`.)
       */
      const sharedFabricDepsList = Object.keys(sharedFabricDeps);

      console.debug(
        `%cApp dependencies for "${appName}" 📦 %c\n👉 Note that this list %cdoes not include dependencies provided by the Fabric Core%c! If you're wondering why, please see the documentation:\n${SUPPORT_URI}\n\n🤗 You can also Slack us (Team Apollo) and we'll explain:\n${SLACK_URI}\n\n`,
        "font-size:1.75em;margin:0.5em 0;",
        "",
        "font-weight:bold;color:red;",
        "",
      );
      console.table(
        Object.fromEntries(
          Object.entries(appDeps).filter(
            ([key]) => !sharedFabricDepsList.includes(key),
          ),
        ),
      );

      const optimizationTips: string[] = [];

      // For each dep in app package.json...
      Object.keys(appDeps).forEach((depName) => {
        const dep = appDeps[depName];
        const isProvided = !!sharedFabricDeps[depName];

        // if dep is trying to use the provided but the dep is not actually provided,
        // warn that our dep is not being shared since fabric does not provide that dep
        if (dep.isUsingProvided && !isProvided) {
          warn(
            [
              `"${appName}" is referencing "${depName}",`,
              "which does not exist as a provided package.",
              'You may need to upgrade "@granular/fabric-core".',
            ].join(" "),
          );
        }
      });

      // For each shared fabric dep...
      Object.keys(sharedFabricDeps).forEach((depName) => {
        const providedDep = sharedFabricDeps[depName];
        const appDep = appDeps[depName];

        if (!appDep) {
          // if we don't have this dependency,
          // we don't have to do any version checking
          return;
        }

        // if dep types don't match, all sorts of problems can arise
        if (providedDep.type !== appDep.type) {
          error(
            [
              `"${appName}" is using "${depName}" as a "${appDep.type}" type`,
              `instead of a "${providedDep.type}" type.`,
              'You may need to upgrade "@granular/fabric-core".',
            ].join(" "),
          );
          return;
        }

        if (providedDep.type === "single") {
          // Get the comparisons of the appDep version and providedDep version
          const comparisonResult = appDep.version.compare(providedDep.version);
          const appDepVersionIsLessThanProvidedDepVersion =
            comparisonResult === -1;
          const appDepVersionIsGreaterThanProvidedDepVersion =
            comparisonResult === 1;

          // if single and using the provided,
          // and our version does not match the provided version,
          // warn that our local dep version does not match the provided
          if (appDep.isUsingProvided) {
            if (appDepVersionIsLessThanProvidedDepVersion) {
              if (appDep.isInAppPackageJson) {
                warn(
                  [
                    `"${appName}" is using an older version (${appDep.versionString}) of "${depName}" locally,`,
                    `which does not match the provided (${providedDep.versionString}).`,
                    `Please upgrade from "${appDep.versionString}" to "${providedDep.versionString}"`,
                    "as soon as possible.",
                  ].join(" "),
                );
              } else {
                warn(
                  [
                    `"${appName}" seems to be using an older version (${appDep.versionString}) of "${depName}" locally,`,
                    `which does not match the provided (${providedDep.versionString}).`,
                    "Please ensure you are up to date on the dependency that provides this dependency.",
                    "Otherwise, this is likely due to conflicting requirements of another dependency.",
                    `In which case, please add "${depName}@${providedDep.versionString}" to your "devDependencies"`,
                    "to ensure you are developing with the same version as the provided package.",
                  ].join(" "),
                );
              }
            } else if (appDepVersionIsGreaterThanProvidedDepVersion) {
              if (appDep.isInAppPackageJson) {
                warn(
                  [
                    `"${appName}" is using a newer version (${appDep.versionString}) of "${depName}" locally,`,
                    `which does not match the provided (${providedDep.versionString}).`,
                    `Please downgrade from "${appDep.versionString}" to "${providedDep.versionString}"`,
                    "as soon as possible.",
                  ].join(" "),
                );
              } else {
                warn(
                  [
                    `"${appName}" seems to be using a newer version (${appDep.versionString}) of "${depName}" locally,`,
                    `which does not match the provided (${providedDep.versionString}).`,
                    "Please ensure you are up to date on the dependency that provides this dependency.",
                    "Otherwise, this is likely due to conflicting requirements of another dependency.",
                    `In which case, please add "${depName}@${providedDep.versionString}" to your "devDependencies"`,
                    "to ensure you are developing with the same version as the provided package.",
                  ].join(" "),
                );
              }
            }
          }

          // if single dep and app is not using the provided,
          // error that we must be using the provided
          if (!appDep.isUsingProvided) {
            if (appDep.isThoughtToBeProvided) {
              error(
                [
                  `"${appName}" is bundling "${depName}"`,
                  "instead of referencing the provided package.",
                  'Please move this to your "devDependencies"',
                  "to reference the provided package instead.",
                ].join(" "),
              );
            } else {
              error(
                [
                  `"${appName}" is bundling "${depName}"`,
                  "instead of referencing the provided package.",
                  'You may need to upgrade "@granular/fabric-core".',
                ].join(" "),
              );
            }
          }
        }

        if (providedDep.type === "flex") {
          // Get the comparisons of the appDep version and providedDep version
          const comparisonResult = appDep.version.compare(providedDep.version);
          const appDepVersionIsLessThanProvidedDepVersion =
            comparisonResult === -1;
          const appDepVersionIsGreaterThanProvidedDepVersion =
            comparisonResult === 1;
          const appDepVersionIsWithinProvidedDepVersionRange = satisfies(
            appDep.version,
            `^${providedDep.versionString}`,
          );

          // if flex and using the provided,
          // and our version does not match the provided version,
          // warn that our local dep version does not match the provided
          if (
            appDep.isUsingProvided &&
            !appDepVersionIsWithinProvidedDepVersionRange
          ) {
            if (appDepVersionIsLessThanProvidedDepVersion) {
              if (appDep.isInAppPackageJson) {
                optimizationTips.push(
                  [
                    `"${appName}" is using an older version (${appDep.versionString}) of "${depName}",`,
                    `which does not match the range of the provided (^${providedDep.versionString}).`,
                    "Thus, the dependency is being bundled",
                    "instead of referencing the provided one.",
                    `Please upgrade from "${appDep.versionString}" to "${providedDep.versionString}"`,
                    "as soon as possible.",
                    "If you do not wish to use the provided dependency,",
                    'please move this to your "dependencies"',
                    "to bundle the package instead.",
                  ].join(" "),
                );
              } else {
                optimizationTips.push(
                  [
                    `"${appName}" seems to be using an older version (${appDep.versionString}) of "${depName}",`,
                    `which does not match the range of the provided (^${providedDep.versionString}).`,
                    "Thus, the dependency is likely being bundled",
                    "instead of referencing the provided one.",
                    "Please ensure you are up to date on the dependency that provides this dependency.",
                    "Otherwise, this is likely due to conflicting requirements of another dependency.",
                    `In which case, please add "${depName}@${providedDep.versionString}" to your "devDependencies"`,
                    "to reference the provided package.",
                    "If you do not wish to use the provided dependency,",
                    'please move this to your "dependencies"',
                    "to bundle the package instead.",
                  ].join(" "),
                );
              }
            }

            if (appDepVersionIsGreaterThanProvidedDepVersion) {
              if (appDep.isInAppPackageJson) {
                optimizationTips.push(
                  [
                    `"${appName}" is using a newer version (${appDep.versionString}) of "${depName}",`,
                    `which does not match the range of the provided (^${providedDep.versionString}).`,
                    "Thus, the dependency is being bundled",
                    "instead of referencing the provided one.",
                    `Please downgrade from "${appDep.versionString}" to "${providedDep.versionString}"`,
                    "as soon as possible.",
                  ].join(" "),
                );
              } else {
                optimizationTips.push(
                  [
                    `"${appName}" seems to be using a newer version (${appDep.versionString}) of "${depName}",`,
                    `which does not match the range of the provided (^${providedDep.versionString}).`,
                    "Thus, the dependency is likely being bundled",
                    "instead of referencing the provided one.",
                    "Please ensure you are up to date on the dependency that provides this dependency.",
                    "Otherwise, this is likely due to conflicting requirements of another dependency.",
                    `In which case, please add "${depName}@${providedDep.versionString}" to your "devDependencies"`,
                    "to reference the provided package.",
                    "If you do not wish to use the provided dependency,",
                    'please move this to your "dependencies"',
                    "to bundle the package instead.",
                  ].join(" "),
                );
              }
            }
          }

          // if flex and not using the provided of a matching version,
          // warn that our dep may be able to reference instead of bundle.
          if (
            !appDep.isUsingProvided &&
            appDepVersionIsWithinProvidedDepVersionRange
          ) {
            optimizationTips.push(
              [
                `"${appName}" seems to be within the version range`,
                `of the provided (^${providedDep.versionString})`,
                `but is likely bundling "${depName}@${appDep.version}"`,
                "instead of referencing the provided package.",
                "To use the provided package instead,",
                'please move this to your "devDependencies"',
              ].join(" "),
            );
          }
        }
      });

      if (optimizationTips.length) {
        log("Optimization Tips\n\n", optimizationTips.join("\n\n"));
      }
    } catch (e) {
      console.error(e);
    }
    // Only run once. `appEnv` (which is `process.env`) is a new value on every
    // render for some reason.
    //
  }, []);
};

let loggedFabricProvidedDependencies = false;

export const useLogFabricProvidedDependencies = (
  appEnv: envService.Env,
): void => {
  useEffect(() => {
    try {
      // Disable when testing
      if (appEnv.NODE_ENV === "TEST") {
        return;
      }

      const appName = envService.getAppName(appEnv);

      // Only run in the container
      if (appName !== "container") {
        console.error(
          `[@granular/fabric-core] useLogFabricProvidedDependencies will not run in "${appName}" as it is only meant to be run in "container"`,
        );
        return;
      }

      // Verify only shown once
      if (loggedFabricProvidedDependencies) {
        return;
      }
      loggedFabricProvidedDependencies = true;

      const sharedFabricDeps = getSharedFabricDeps();

      /**
       * Show a nice message and a lovely table listing the dependencies. Note
       * that we are only showing the package name and its version. Nobody
       * except us (Apollo) cares about all the other properties...
       */
      console.debug(
        `%cDependencies provided by Fabric Core 🚀%c\n👉 Anything on this list %cis a singleton dependency and %cwill override its counterpart in your application%c! If that stuff in red doesn't make sense, please see the documentation:\n${SUPPORT_URI}\n\n🤗 You can also Slack us (Team Apollo) and we'll help you out:\n${SLACK_URI}\n\n`,
        "font-size:1.75em;margin:0.5em 0;",
        "",
        "font-weight:bold;color:red;",
        "font-weight:bold;font-style:italic;color:red;",
        "",
      );
      console.table(sharedFabricDeps);
    } catch (e) {
      console.error(e);
    }

    // Only run once. `appEnv` (which is `process.env`) is a new value on every
    // render for some reason.
  }, []);
};
